import clsx from 'clsx';
import { StarIcon } from '../../../assets/icons';
import Button from '../../../components/Button';
import { TradePair } from '../../../context/chart/chartTypes';
import useChartState from '../../../context/chart/useChartState';
import { formatPercentages, formatUSD } from '../../../helpers';
import useTokenStats from '../../../hooks/web3/useTokenStats';

type ExchangeMarketListRowProps = {
    pool: TradePair & { isFavourited: boolean };
    toggleFavourite: React.Dispatch<string>;
};

type Props = {
    pool: TradePair & { isFavourited: boolean };
};

const ExchangeMarketListRow: React.FC<ExchangeMarketListRowProps> = ({
    pool,
    toggleFavourite,
}) => {
    const { name, asset, isFavourited, maxLeverage } = pool;

    const { tvWidget, selectedTradePair, isChartReady, setSelectedTradePair } =
        useChartState();

    const splitName = name.replaceAll(' ', '').split('/');
    const isActive = asset.symbol === selectedTradePair?.asset.symbol;

    const handleFavourite = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.currentTarget.blur();
        toggleFavourite(name);
    };

    const handleEnterKeydown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handlePoolChange();
        }
    };

    const handlePoolChange = () => {
        if (!isChartReady) return;
        setSelectedTradePair(pool);
        tvWidget?.activeChart()?.setSymbol(pool.chartSymbol);
    };

    return (
        <li
            className={clsx('row', { active: isActive, disabled: !isChartReady })}
            tabIndex={isActive ? -1 : 0}
            onClick={handlePoolChange}
            onKeyDown={handleEnterKeydown}
        >
            <div className='option'>
                <Button
                    onClick={handleFavourite}
                    className={isFavourited ? 'active' : undefined}
                >
                    <StarIcon />
                </Button>
                <span className='market-name'>
                    {splitName[0]}/<span className='collateral'>{splitName[1]}</span>
                </span>
                <span className='leverage'>{formatPercentages(maxLeverage, 0)}x</span>
            </div>
            <Price pool={pool} />
            <PriceChange pool={pool} />
        </li>
    );
};

export default ExchangeMarketListRow;

const Price: React.FC<Props> = ({ pool }) => {
    const tokenStats = useTokenStats();

    const { chartSymbol, decimalFormatting } = pool;

    return (
        <span className='price'>
            {tokenStats?.[chartSymbol]?.price
                ? formatUSD(tokenStats[chartSymbol]?.price || 0, {
                      minDecimals: 2,
                      maxDecimals: decimalFormatting,
                  })
                : '-'}
        </span>
    );
};

const PriceChange: React.FC<Props> = ({ pool }) => {
    const tokenStats = useTokenStats();

    const { chartSymbol } = pool;

    const change = tokenStats?.[chartSymbol]?.priceChangePercent;
    const isPositiveDifference = change ? change >= 0 : true;

    return (
        <span
            className={`change ${
                change ? (isPositiveDifference ? 'increased' : 'decreased') : ''
            }`}
        >
            {change ? (
                <>
                    {isPositiveDifference ? '+' : '-'}
                    {formatPercentages(Math.abs(change), 2)}%
                </>
            ) : (
                '-'
            )}
        </span>
    );
};
